import React from 'react';
import { orderBy } from 'lodash';
import { Tooltip, useTheme } from '@mui/material';

import { Icon, useSettings } from 'app/main/components';
import { renderCellWithTooltip } from 'app/main/components/page-layouts/view/table/ViewTableUtils';

function DescriptionText({ row, isEditable }: { row: any; isEditable: boolean }) {
	const { t } = useSettings();
	const theme = useTheme();

	const showError = !!row.orli_error && isEditable;
	const errorType = row.orli_error === 1 ? 'OUTDATED_PRICE' : 'UNAVAILABLE';
	const errorColor = row.orli_error === 1 ? theme.palette.warning.main : theme.palette.error.main;

	return (
		<>
			{showError ? (
				<div className="mr-4">
					<Tooltip title={showError ? t(`DETAILS_ORDERS_LINES_FIELD_ERROR_${errorType}`) : undefined} arrow>
						<Icon icon={row.orli_error === 1 ? 'warning-outline' : 'block'} color={errorColor} />
					</Tooltip>
				</div>
			) : (
				<></>
			)}
			{renderCellWithTooltip({
				className: row.orli_type === 'note' ? 'italic' : undefined,
				value: row.orli_description,
				to: row.orli_type === 'item' ? { module: 'items', uid: row.orli_item_uid } : undefined
			})}
		</>
	);
}

export function getDefaultQuery(orderPricingGroupId: string, orderCompUid: string) {
	return {
		or: [
			{
				and: [
					orderPricingGroupId
						? { equals: [{ var: 'itpr_pric_id' }, orderPricingGroupId] }
						: { is_null: { var: 'itpr_pric_id' } },
					{ is_null: { var: 'itpr_comp_uid' } }
				]
			},
			{ and: [{ is_null: { var: 'itpr_pric_id' } }, { equals: [{ var: 'itpr_comp_uid' }, orderCompUid] }] }
		]
	};
}

export function getOrderQuantities(orderMultiples?: number, orderQtyMin?: number) {
	return new Array(20)
		.fill(orderMultiples || 1)
		.map((value, index) => value * (index + 1))
		.filter(quantity => (orderQtyMin ? quantity >= orderQtyMin : true));
}

export function calcUnitPrice(basePrice: number, discountPercentage: number) {
	// DO NOT REMOVE .toFixed(2) because x.xx * 100 returns xxx.000000001
	return Math.ceil(Number.parseFloat((basePrice * (100 - discountPercentage)).toFixed(2))) / 100;
}

export function calcPricing(model: any, discountPercentage: number) {
	const quantity = model.orli_quantity || 0;

	const lotPricing = quantity
		? orderBy(model.pricings_lots, 'itpr_qty_min', 'desc').find(lot => lot.itpr_qty_min <= quantity)
		: model.pricings_lots?.[0];

	const newBasePrice = lotPricing?.itpr_base_price;
	const newDiscount = lotPricing?.itpr_discountable ? discountPercentage : undefined;
	const newBasePriceFinal = (model.orli_base_price_custom ?? newBasePrice) || 0;
	const newDiscountFinal = (model.orli_discount_custom ?? newDiscount) || 0;
	const newUnitPrice = calcUnitPrice(newBasePriceFinal, newDiscountFinal);
	const newExpenseFinal = model.orli_unit_expenses_custom || 0;

	return {
		...model,
		orli_item_uid: lotPricing?.itpr_item_uid || model.orli_item_uid || model.itpr_item_uid,
		orli_itpr_uid: lotPricing?.itpr_uid,
		orli_quantity: quantity,
		orli_base_price: newBasePrice,
		orli_base_price_custom: model.orli_base_price_custom,
		orli_base_price_final: newBasePriceFinal,
		orli_discount: newDiscount,
		orli_discount_custom: model.orli_discount_custom,
		orli_discount_final: newDiscountFinal,
		orli_unit_price: newUnitPrice,
		orli_tot_value_tax_excl: newUnitPrice * quantity,
		orli_unit_expenses_custom: model.orli_unit_expenses_custom,
		orli_unit_expenses_final: newExpenseFinal,
		orli_tot_expenses: newExpenseFinal * quantity
	};
}

export function getRowClassName(row: any, isEditable: boolean) {
	if (!!row.orli_error && isEditable) {
		if (row.orli_error === 1) return 'OrderLinesTab-UpdatedPriceRow';
		if (row.orli_error === 2) return 'OrderLinesTab-UnavailableRow';
	}
	return '';
}

export function renderDescription(params: { row: any; isEditable: boolean }) {
	return <DescriptionText {...params} />;
}

export function renderUpdatableValue(params: {
	value?: number;
	newValue?: number;
	renderValue: ({ value }: { value?: number }) => React.ReactNode;
	showNewValue?: boolean;
}) {
	if (params.newValue && params.newValue >= 0 && params.value !== params.newValue && params.showNewValue) {
		const newValue = params.newValue !== 0 && params.renderValue({ value: params.newValue });
		const oldValue = params.renderValue({ value: params.value || 0 });
		return renderCellWithTooltip({
			value: (
				<>
					{newValue}&nbsp;<s>{oldValue}</s>
				</>
			)
		});
	}

	return params.renderValue({ value: params.value || 0 });
}

export default {
	calcPricing,
	calcUnitPrice,
	getDefaultQuery,
	getOrderQuantities,
	getRowClassName,
	renderDescription,
	renderUpdatableValue
};
